import axios from 'axios';
import { resetRedux } from '../app/global';
import { AsyncDispatch } from '../../../types/global';
import { getApiRoot, getNonGwApiRoot } from '../../constants/ParamountReactConstants';
import * as utils from '../../utils/Utils';
import { CognitoTokens, MfaChallengeResponse, SsoDiscoverResponse } from '../../../types/user';

const COGNITO_TOKEN_KEY = 'paramount-cognito-tokens';
const TOKEN_KEY = 'paramount-token';
const SSO_STATE_KEY = 'paramount-sso-state';

function isMfaChallenge(response: any): response is MfaChallengeResponse {
  return response.challenge === true && response.challenge_name === 'SOFTWARE_TOKEN_MFA';
}

function storeCognitoTokens(tokens: CognitoTokens) {
  const tokenData = {
    ...tokens,
    expires_at: Date.now() + tokens.expires_in * 1000,
  };
  localStorage.setItem(COGNITO_TOKEN_KEY, JSON.stringify(tokenData));
}

export function getCognitoTokens(): (CognitoTokens & { expires_at: number }) | null {
  const stored = localStorage.getItem(COGNITO_TOKEN_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

export function clearCognitoTokens() {
  localStorage.removeItem(COGNITO_TOKEN_KEY);
}

export function removeTokenAndUser() {
  localStorage.removeItem(TOKEN_KEY);
  clearCognitoTokens();
  return {
    type: 'REMOVE_USER',
  };
}

export function fetchUsers(userIds?: number[]) {
  const params = utils.createURL([{ name: 'user_ids', values: userIds }]);
  return {
    type: 'FETCH_USERS',
    payload: axios.get(`${getApiRoot()}/accounts/users${params}`),
    meta: { userIds },
  };
}

export function fetchUserNames() {
  return {
    type: 'FETCH_USER_NAMES',
    payload: axios.get(`${getNonGwApiRoot()}/accounts/user_names`),
  };
}

export function login(username: string, password: string) {
  return (dispatch: AsyncDispatch) => {
    dispatch(resetRedux());
    return axios
      .post(`${getNonGwApiRoot()}/users/login/v3`, {
        email: username,
        password,
      })
      .then(res => {
        const data = res.data;

        if (isMfaChallenge(data)) {
          dispatch({
            type: 'LOGIN_MFA_REQUIRED',
            payload: {
              session: data.session,
              email: data.email,
              challengeName: data.challenge_name,
            },
          });
          return { mfaRequired: true, ...data };
        }

        const token = data.token || data.access_token;
        localStorage.setItem(TOKEN_KEY, JSON.stringify(token));

        if (data.access_token && data.refresh_token) {
          storeCognitoTokens({
            access_token: data.access_token,
            id_token: data.id_token,
            refresh_token: data.refresh_token,
            expires_in: data.expires_in,
            token_type: data.token_type,
          });
        }

        dispatch({
          type: 'LOGIN_FULFILLED',
          payload: { data },
        });

        return { mfaRequired: false, data };
      })
      .catch(error => {
        dispatch({
          type: 'LOGIN_REJECTED',
          payload: error.response,
        });
        throw error;
      });
  };
}

export function verifyMfaCode(email: string, session: string, code: string) {
  return (dispatch: AsyncDispatch) => {
    // Set pending state to show loading indicator
    dispatch({ type: 'MFA_VERIFY_PENDING' });

    return axios
      .post(`${getNonGwApiRoot()}/auth/mfa/challenge`, {
        email,
        session,
        code,
      })
      .then(res => {
        // Response structure: { success: true, data: { access_token, ..., user: {...} } }
        const responseData = res.data.data || res.data;
        const token = responseData.token || responseData.access_token;
        localStorage.setItem(TOKEN_KEY, JSON.stringify(token));

        if (responseData.access_token && responseData.refresh_token) {
          storeCognitoTokens({
            access_token: responseData.access_token,
            id_token: responseData.id_token,
            refresh_token: responseData.refresh_token,
            expires_in: responseData.expires_in,
            token_type: responseData.token_type,
          });
        }

        // Fetch full user data using the new token
        const config = {
          headers: { 'Paramount-Access-Token': token },
        };
        return axios.get(`${getNonGwApiRoot()}/users`, config);
      })
      .then(userRes => {
        const userData = userRes.data;
        // MFA_VERIFY_FULFILLED clears MFA state AND sets user in one action
        dispatch({
          type: 'MFA_VERIFY_FULFILLED',
          payload: { data: userData },
        });
        return userData;
      })
      .catch(error => {
        dispatch({
          type: 'MFA_VERIFY_REJECTED',
          payload: error.response,
        });
        throw error;
      });
  };
}

export function getMfaStatus() {
  return {
    type: 'GET_MFA_STATUS',
    payload: axios.get(`${getNonGwApiRoot()}/auth/mfa/status`),
  };
}

export function setupMfa() {
  return {
    type: 'SETUP_MFA',
    payload: axios.post(`${getNonGwApiRoot()}/auth/mfa/setup`),
  };
}

export function verifyMfaSetup(code: string) {
  return {
    type: 'VERIFY_MFA_SETUP',
    payload: axios.post(`${getNonGwApiRoot()}/auth/mfa/verify-setup`, {
      code,
    }),
  };
}

export function disableMfa(code: string) {
  return {
    type: 'DISABLE_MFA',
    payload: axios.post(`${getNonGwApiRoot()}/auth/mfa/disable`, { code }),
  };
}

export function refreshToken() {
  return (dispatch: AsyncDispatch) => {
    const cognitoTokens = getCognitoTokens();
    const token = localStorage.getItem(TOKEN_KEY);
    const email = token ? JSON.parse(token) : '';

    if (!cognitoTokens?.refresh_token) {
      return Promise.reject(new Error('No refresh token available'));
    }

    return axios
      .post(`${getNonGwApiRoot()}/auth/refresh`, {
        refresh_token: cognitoTokens.refresh_token,
        email,
      })
      .then(res => {
        const data = res.data.data;

        localStorage.setItem(TOKEN_KEY, JSON.stringify(data.access_token));

        storeCognitoTokens({
          access_token: data.access_token,
          id_token: data.id_token,
          refresh_token: cognitoTokens.refresh_token,
          expires_in: data.expires_in,
          token_type: data.token_type,
        });

        return data;
      })
      .catch(error => {
        dispatch(removeTokenAndUser());
        throw error;
      });
  };
}

export function clearMfaState() {
  return {
    type: 'CLEAR_MFA_STATE',
  };
}

export function discoverSsoProvider(email: string): Promise<SsoDiscoverResponse> {
  return axios
    .post(`${getNonGwApiRoot()}/auth/sso/discover`, {
      email: email.toLowerCase().trim(),
      redirect_uri: `${window.location.origin}/auth/callback`,
    })
    .then(res => {
      const data = res.data;
      // Store state for CSRF validation when user returns
      if (data.state) {
        sessionStorage.setItem(SSO_STATE_KEY, data.state);
      }
      return data;
    });
}

export function getSsoState(): string | null {
  return sessionStorage.getItem(SSO_STATE_KEY);
}

export function clearSsoState(): void {
  sessionStorage.removeItem(SSO_STATE_KEY);
}

export function exchangeSSOCode(code: string) {
  return (dispatch: AsyncDispatch) => {
    dispatch(resetRedux());
    dispatch({ type: 'SSO_LOGIN_PENDING' });

    return axios
      .post(`${getNonGwApiRoot()}/auth/sso/callback`, {
        code,
        redirect_uri: `${window.location.origin}/auth/callback`,
      })
      .then(res => {
        const responseData = res.data.data || res.data;
        const token = responseData.token || responseData.access_token;
        localStorage.setItem(TOKEN_KEY, JSON.stringify(token));

        if (responseData.access_token && responseData.refresh_token) {
          storeCognitoTokens({
            access_token: responseData.access_token,
            id_token: responseData.id_token,
            refresh_token: responseData.refresh_token,
            expires_in: responseData.expires_in,
            token_type: responseData.token_type,
          });
        }

        // Set axios default header for subsequent requests
        axios.defaults.headers.common['Paramount-Access-Token'] = token;

        // Clear SSO state after successful exchange
        clearSsoState();

        // Fetch full user data
        return axios.get(`${getNonGwApiRoot()}/users`);
      })
      .then(userRes => {
        const userData = userRes.data;
        dispatch({
          type: 'SSO_LOGIN_FULFILLED',
          payload: { data: userData },
        });
        return userData;
      })
      .catch(error => {
        dispatch({
          type: 'SSO_LOGIN_REJECTED',
          payload: error.response,
        });
        throw error;
      });
  };
}

export function createUser({
  emailNotification,
  firstName,
  lastName,
  email,
  password,
  admin,
}: {
  emailNotification: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  admin: number;
}) {
  return {
    type: 'CREATE_USER',
    payload: axios.post(`${getApiRoot()}/users`, {
      email_notification: emailNotification,
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      admin,
    }),
  };
}

export function updateUser({
  id,
  firstName,
  lastName,
  email,
  admin,
}: {
  id: number;
  firstName: string;
  lastName: string;
  email?: string;
  admin?: number;
}) {
  const params = utils.createURL([{ name: 'user_id', values: id }]);

  return {
    type: 'UPDATE_USER',
    payload: axios.patch(`${getApiRoot()}/users${params}`, {
      first_name: firstName,
      last_name: lastName,
      email,
      admin,
    }),
  };
}

export function register({
  firstName,
  lastName,
  email,
  otherCompany,
  password,
  parentName,
  parentId,
}: {
  firstName: string;
  lastName: string;
  email: string;
  otherCompany: string;
  password: string;
  parentName: string;
  parentId: number;
}) {
  return {
    type: 'REGISTER',
    payload: axios.post(`${getApiRoot()}/users/register`, {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      parent_id: parentId || '',
      parent_name: parentName,
      ...(otherCompany ? { other_company: otherCompany } : {}),
    }),
  };
}

export function registerReceiver({
  firstName,
  lastName,
  email,
  password,
  receiverName,
}: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  receiverName: string;
}) {
  return {
    type: 'REGISTER',
    payload: axios.post(`${getApiRoot()}/users/receiver_register`, {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      receiver_name: receiverName,
    }),
  };
}

export function logout() {
  localStorage.removeItem(TOKEN_KEY);
  clearCognitoTokens();
  if ((window as any).Intercom) (window as any).Intercom('shutdown');

  return {
    type: 'LOGOUT',
    payload: axios.post(`${getNonGwApiRoot()}/users/logout`),
  };
}

export function loadUserByToken() {
  const token = localStorage.getItem(TOKEN_KEY) ? JSON.parse(localStorage.getItem(TOKEN_KEY)!) : '';
  const config = {
    headers: { 'Paramount-Access-Token': token },
  };

  return (dispatch: AsyncDispatch) => {
    dispatch(resetRedux());
    return dispatch({
      type: 'LOAD_USER_BY_TOKEN',
      payload: axios.get(`${getNonGwApiRoot()}/users`, config),
    });
  };
}

export function refetchUserByToken() {
  return {
    type: 'REFETCH_USER',
    payload: axios.get(`${getNonGwApiRoot()}/users`),
  };
}

export function loadUserByOktaToken(token: string) {
  const params = utils.createURL([{ name: 'sso_token', values: token }]);

  return (dispatch: AsyncDispatch) => {
    dispatch(resetRedux());
    dispatch({
      type: 'LOAD_USER_BY_TOKEN',
      payload: axios.get(`${getApiRoot()}/sso/authenticate${params}`),
    }).then(res => {
      const resToken = res.value.data.token;
      localStorage.setItem(TOKEN_KEY, JSON.stringify(resToken));
    });
  };
}

export function updateAccountInformations(
  userId: number,
  email: string,
  emailNotification?: number,
  firstName?: string,
  lastName?: string
) {
  const params = utils.createURL([{ name: 'user_id', values: userId }]);

  const body = {
    email_notification: emailNotification,
    first_name: firstName,
    last_name: lastName,
    email,
  };

  return {
    type: 'UPDATE_ACCOUNT_INFORMATIONS',
    payload: axios.patch(`${getApiRoot()}/users${params}`, body),
  };
}

export function resetUserPassword(email: string) {
  return {
    type: 'UPDATE_USER_STATE',
    payload: axios.post(`${getApiRoot()}/users/reset_password`, { email }),
  };
}

export function forgotPassword(email: string) {
  return {
    type: 'FORGOT_PASSWORD',
    payload: axios.post(`${getApiRoot()}/users/reset_password`, { email }),
  };
}

export function resetPassword(resetToken: string, newPassword: string) {
  return {
    type: 'RESET_PASSWORD',
    payload: axios.patch(`${getApiRoot()}/users/reset_password`, {
      reset_token: resetToken,
      new_password: newPassword,
    }),
  };
}

export function resetPasswordWithCode(email: string, code: string, newPassword: string) {
  return {
    type: 'RESET_PASSWORD',
    payload: axios.patch(`${getApiRoot()}/users/reset_password`, {
      email,
      code,
      new_password: newPassword,
    }),
  };
}

export function updatePassword(currentPw: string, newPw: string) {
  const body = {
    current_password: currentPw,
    new_password: newPw,
  };

  return {
    type: 'UPDATE_PASSWORD',
    payload: axios.patch(`${getApiRoot()}/users/password`, body),
  };
}

export function updateUserState(userId: number, active: number) {
  const params = utils.createURL([{ name: 'user_id', values: userId }]);

  return {
    type: 'UPDATE_USER_STATE',
    payload: axios.patch(`${getApiRoot()}/users${params}`, { active }),
  };
}

export function fetchPermissions() {
  return {
    type: 'FETCH_PERMISSIONS',
    payload: axios.get(`${getNonGwApiRoot()}/accounts/permissions`),
  };
}

export function fetchRoles() {
  return {
    type: 'FETCH_ROLES',
    payload: axios.get(`${getNonGwApiRoot()}/user_roles`),
  };
}

export function fetchMappedUserRoles(userId: number) {
  const params = utils.createURL([{ name: 'user_id', values: userId }]);

  return {
    type: 'FETCH_MAPPED_USER_ROLES',
    payload: axios.get(`${getNonGwApiRoot()}/users${params}`),
  };
}

export const deleteUserRole = (id: number) => ({
  type: 'DELETE_USER_ROLE',
  payload: axios.delete(`${getApiRoot()}/user_roles/${id}`),
  meta: { roleId: id },
});

export function updateUserRole(roles: any) {
  const body = { user_roles: [roles] };

  return {
    type: 'UPDATE_USER_ROLE',
    payload: axios.patch(`${getApiRoot()}/user_roles`, body),
  };
}

export function updateMappedUserPermissions(user: any) {
  const body = {
    users: [user],
  };

  return {
    type: 'UPDATE_MAPPED_USER_PERMISSIONS',
    payload: axios.patch(`${getApiRoot()}/accounts/users`, body),
  };
}

export function fetchAccessibleAccounts() {
  return {
    type: 'FETCH_ACCESSIBLE_ACCOUNTS',
    payload: axios.get(`${getNonGwApiRoot()}/accounts/accessible_accounts`),
  };
}

export function switchAccount(targetAccountId: number, reason?: string) {
  const body = {
    target_account_id: targetAccountId,
    ...(reason ? { reason } : {}),
  };

  return {
    type: 'SWITCH_ACCOUNT',
    payload: axios.post(`${getNonGwApiRoot()}/accounts/switch_account`, body),
  };
}

export function switchBackToOriginalAccount() {
  return {
    type: 'SWITCH_BACK_ACCOUNT',
    payload: axios.post(`${getNonGwApiRoot()}/accounts/switch_back`, {}),
  };
}

export function fetchAdminUsersView() {
  return {
    type: 'FETCH_ADMIN_USERS_VIEW',
    payload: axios.get(`${getNonGwApiRoot()}/accounts/users/admin_view`),
  };
}

export function updateUserAccountAssignments(
  userId: number,
  accountAssignments: {
    account_id: number;
    admin: number;
    user_role_ids: number[];
    brand_permissions: { brand_id: number; maintenance_type_id: number }[];
  }[]
) {
  return {
    type: 'UPDATE_USER_ACCOUNT_ASSIGNMENTS',
    payload: axios.patch(`${getNonGwApiRoot()}/accounts/users/${userId}/account_assignments`, {
      account_assignments: accountAssignments,
    }),
    meta: { userId },
  };
}

export function fetchAccountBrands(accountId: number) {
  return {
    type: 'FETCH_ACCOUNT_BRANDS',
    payload: axios.get(`${getNonGwApiRoot()}/accounts/${accountId}/brands`),
    meta: { accountId },
  };
}

export function fetchAccountRoles(accountId: number) {
  return {
    type: 'FETCH_ACCOUNT_ROLES',
    payload: axios.get(`${getNonGwApiRoot()}/accounts/${accountId}/roles`),
    meta: { accountId },
  };
}
